import { MouseEvent } from 'react';

import clsx from 'clsx';

import {
  mergeSx,
  SxProps,
  Tooltip,
  Typography,
  Box,
} from '@cast/design-system';

import { Link } from 'components/router';

import { sidebarItemClassNames } from './sidebarClassNames';
import { SidebarItemCaret } from './SidebarItemCaret';
import type { SidebarIconComponent } from './sidebarNavConfig';
import {
  mergeSidebarItemSx,
  sidebarItemGap,
  sidebarItemLinkSx,
  SidebarItemVisualState,
  sidebarNestedGap,
} from './sidebarStyles';

type SidebarItemProps = {
  title: string;
  to?: string;
  icon?: SidebarIconComponent;
  active?: boolean;
  /** Parent with an active child: selection color, no pill. */
  indicated?: boolean;
  expanded?: boolean;
  isNested?: boolean;
  showCaret?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
  onToggle?: () => void;
  testId?: string;
  className?: string;
  sx?: SxProps;
};

export const SidebarItem = ({
  title,
  to,
  icon: Icon,
  active = false,
  indicated = false,
  expanded = false,
  isNested = false,
  showCaret = false,
  disabled = false,
  disabledTooltip,
  onToggle,
  testId,
  className,
  sx,
}: SidebarItemProps) => {
  const state: SidebarItemVisualState = { active, indicated };

  const navigateTo = to && !expanded && !indicated ? to : undefined;
  const isToggleButton = Boolean(onToggle && !navigateTo);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (disabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (isToggleButton && onToggle) {
      event.preventDefault();
      event.stopPropagation();
      onToggle();
    }
  };

  const row = (
    <Box
      component={isToggleButton ? 'button' : 'div'}
      type={isToggleButton ? 'button' : undefined}
      onClick={isToggleButton || disabled ? handleClick : undefined}
      aria-expanded={showCaret ? expanded : undefined}
      className={clsx(sidebarItemClassNames.root, !navigateTo && className)}
      data-testid={!navigateTo ? testId ?? `sidebar-item-${title}` : undefined}
      sx={mergeSidebarItemSx(
        { ...state, isNested },
        mergeSx(
          !navigateTo ? sx : undefined,
          disabled
            ? { opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'auto' }
            : undefined
        )
      )}
    >
      <Box
        className={sidebarItemClassNames.leading}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: isNested ? sidebarNestedGap : sidebarItemGap,
          minWidth: 0,
          flex: 1,
        }}
      >
        {Icon ? (
          <Box
            className={sidebarItemClassNames.icon}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '16px',
              height: '16px',
              flexShrink: 0,
              color: 'inherit',
            }}
          >
            <Icon size={16} weight="regular" color="currentColor" />
          </Box>
        ) : null}
        <Typography
          className={sidebarItemClassNames.label}
          component="span"
          variant="P14M"
          noWrap
          data-testid="title"
          sx={{ color: 'inherit', lineHeight: '22px' }}
        >
          {title}
        </Typography>
      </Box>

      {showCaret ? (
        <Box
          className={sidebarItemClassNames.trailing}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            color: 'inherit',
          }}
        >
          <SidebarItemCaret expanded={expanded} />
        </Box>
      ) : null}
    </Box>
  );

  if (!navigateTo) {
    return (
      <Tooltip
        title={disabled ? disabledTooltip : ''}
        disableHoverListener={!disabled}
      >
        {row}
      </Tooltip>
    );
  }

  if (disabled) {
    return (
      <Tooltip title={disabledTooltip}>
        <Box
          className={clsx(sidebarItemClassNames.link, className)}
          data-testid={testId ?? `sidebar-item-${title}`}
          sx={mergeSx(sidebarItemLinkSx, sx, {
            opacity: 0.5,
            cursor: 'not-allowed',
            pointerEvents: 'auto',
          })}
        >
          {row}
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box
      component={Link}
      to={navigateTo}
      aria-current={active ? 'page' : undefined}
      className={clsx(sidebarItemClassNames.link, className)}
      data-testid={testId ?? `sidebar-item-${title}`}
      sx={mergeSx(sidebarItemLinkSx, sx)}
    >
      {row}
    </Box>
  );
};
